import { SupportedLanguages } from '~/config/i18n'
import { UtmFields } from '~/utils/utm-fields'
import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { action as emailCheckAction } from '~/routes/email-check'
import { useFetcher } from 'react-router'
import { debounce } from 'lodash-es'
import { trialText } from '~/config'
import { Select } from './select'
import { ActionData } from '~/utils/hubspot'
import { countries as countryList } from 'countries-list'
import { leadSource, leadSourceReferral } from '~/components/forms/options'
declare const window: Window & {
  lintrk?: (str: string, data: Record<string, unknown>) => void
}

type Props = {
  /**
   * @default 'Request Free Trial'
   */
  buttonLabel?: string
  /**
   * @default en
   */
  lang?: SupportedLanguages
  children?: ReactNode
  campaignCode?: string
  formCta?: string
  actionData?: ActionData
  /** Optional override for the lead source + referral input section */
  customReferralField?: ReactNode
  formTitle?: string
  /** Adds the phone and conditional "Other" fields used by demo forms. */
  includeDemoFields?: boolean
}

type FormLabelTranslation = {
  formTitle: string
  secondFormTitle: string
  firstName: string
  lastName: string
  email: string
  company: string
  headquarters: string
  heardFrom?: string
  whoReferred?: string
}

const labelsLang: Record<string, FormLabelTranslation> = {
  en: {
    formTitle: 'Schedule a 1:1 meeting with our team',
    secondFormTitle: 'Request a free trial',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Work email',
    company: 'Company name',
    headquarters: 'Headquarters',
    heardFrom: 'How did you hear about us?',
    whoReferred: 'Who referred you to us?',
  },
  fr: {
    formTitle: "Demander une version d'essai gratuite",
    secondFormTitle: "Demander une version d'essai gratuite",
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    company: "Nom de l'entreprise",
    headquarters: 'Siège social',
    heardFrom: 'Comment avez-vous entendu parler de nous?',
    whoReferred: 'Comment avez-vous entendu parler de nous?',
  },
}

export const FormBasics = ({
  buttonLabel = 'Request Free Trial',
  campaignCode = '',
  formCta = '',
  lang = SupportedLanguages.English,
  children,
  actionData,
  customReferralField,
  formTitle,
  includeDemoFields = false,
}: Props) => {
  const defaultCountryCode =
    lang === SupportedLanguages.French ? 'France' : 'United States'

  // Use useState + useEffect to avoid hydration mismatch with Intl.DisplayNames
  const [countries, setCountries] = useState(() => {
    // Server-side: use English country names to avoid hydration issues
    return Object.keys(countryList)
      .map((code) => ({
        name: countryList[code as keyof typeof countryList].name,
        translatedName: countryList[code as keyof typeof countryList].name,
      }))
      .sort((a, b) => a.translatedName.localeCompare(b.translatedName, 'en'))
  })

  useEffect(() => {
    // Client-side: use localized country names
    const locale = lang === SupportedLanguages.French ? 'fr-FR' : 'en-US'
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
    const localizedCountries = Object.keys(countryList)
      .map((code) => ({
        name: countryList[code as keyof typeof countryList].name,
        translatedName:
          displayNames.of(code) ||
          countryList[code as keyof typeof countryList].name,
      }))
      .sort((a, b) => a.translatedName.localeCompare(b.translatedName, locale))
    setCountries(localizedCountries)
  }, [lang])

  const [source, setSource] = useState('')
  const handleSetSource = (event: ChangeEvent<HTMLSelectElement>) =>
    setSource(event.target.value)

  const labels = labelsLang[lang.split('-')[0]]

  const fetcher = useFetcher<typeof emailCheckAction>()

  const [lastCheckedEmail, setLastCheckedEmail] = useState('')

  const formRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const [phoneError, setPhoneError] = useState('')
  const [canSubmit, setCanSubmit] = useState(false)

  const refreshCanSubmit = () => {
    const email = emailInputRef.current?.value?.trim()
    const emailGate =
      !!email && fetcher.state === 'idle' && fetcher.data?.valid === true
    setCanSubmit(!!formRef.current?.checkValidity() && emailGate)
  }

  const validatePhone = () => {
    const input = phoneInputRef.current
    if (!input) return true

    const value = input.value.trim()
    const validCharacters = /^\+?[\d\s().-]+$/.test(value)
    const digitCount = value.replace(/\D/g, '').length
    const message =
      !value || (validCharacters && digitCount >= 7 && digitCount <= 15)
        ? ''
        : validCharacters
          ? 'Phone number must contain 7 to 15 digits'
          : 'Enter a valid phone number'

    input.setCustomValidity(message)
    setPhoneError(message)
    refreshCanSubmit()
    return !message
  }

  const onPhoneChange = () => {
    phoneInputRef.current?.setCustomValidity('')
    setPhoneError('')
    refreshCanSubmit()
  }
  const onEmailBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const email = event.target.value
    if (email === lastCheckedEmail) return
    fetcher.submit({ email }, { action: '/email-check', method: 'post' })
    setLastCheckedEmail(email)
  }

  // oxlint-disable-next-line exhaustive-deps
  const onEmailChange = useMemo(() => debounce(onEmailBlur, 1000), [])

  useEffect(() => {
    if (!emailInputRef.current) return
    emailInputRef.current.setCustomValidity(fetcher.data?.message ?? '')
  }, [fetcher.data])

  useEffect(() => {
    const email = emailInputRef.current?.value?.trim()
    if (!email) return
    if (email !== lastCheckedEmail) {
      fetcher.submit({ email }, { action: '/email-check', method: 'post' })
      setLastCheckedEmail(email)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const form = formRef.current
    if (!form) return

    const update = () => {
      const basicValid = form.checkValidity() // respects required/type + setCustomValidity
      const email = emailInputRef.current?.value?.trim()
      const emailGate =
        !!email && fetcher.state === 'idle' && fetcher.data?.valid === true

      setCanSubmit(basicValid && emailGate)
    }

    // initial check
    update()

    // re-check on user edits
    form.addEventListener('input', update)
    form.addEventListener('change', update)

    // late autofill pass (Chrome/Safari)
    const raf = requestAnimationFrame(update)
    const t = setTimeout(update, 400)

    return () => {
      form.removeEventListener('input', update)
      form.removeEventListener('change', update)
      cancelAnimationFrame(raf)
      clearTimeout(t)
    }
  }, [fetcher.state, fetcher.data]) // re-evaluate when async validation changes

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!validatePhone()) {
      event.preventDefault()
      phoneInputRef.current?.reportValidity()
      return
    }
    if (!canSubmit) return
    const data = { conversion_id: trialText.conversionId }
    // GTM injects LinkedIn's tag, so it is absent whenever GTM is blocked or
    // still loading. The form posts either way.
    window.lintrk?.('track', data)
  }

  const resolvedTitle = formTitle ?? labels.secondFormTitle

  return (
    <form ref={formRef} method="POST" onSubmit={onSubmit}>
      {(actionData?.error ||
        (actionData?.fieldErrors &&
          Object.values(actionData.fieldErrors).flat()[0])) && (
        <div className="form-error">
          {actionData.error || Object.values(actionData.fieldErrors!).flat()[0]}
        </div>
      )}
      <input
        type="hidden"
        name="pageName"
        value={typeof document !== 'undefined' ? document.title : ''}
      />
      <input
        type="hidden"
        name="referrer"
        value={typeof document !== 'undefined' ? document.referrer : ''}
      />
      <input type="hidden" name="campaign_code" value={campaignCode} />
      <input type="hidden" name="form_cta" value={formCta} />
      <UtmFields />
      <h4 className="form-title">{resolvedTitle}</h4>
      <fieldset className="choices-columns">
        <div className="field">
          <label htmlFor="firstname">
            {labels.firstName} <span className="required">*</span>
          </label>
          <input type="text" name="firstname" required />
        </div>
        <div className="field">
          <label htmlFor="lastname">
            {labels.lastName} <span className="required">*</span>
          </label>
          <input type="text" name="lastname" required />
        </div>
      </fieldset>

      <div className="field">
        <label htmlFor="email">
          {labels.email} <span className="required">*</span>
        </label>
        <input
          id="email"
          type="email"
          name="email"
          size={32}
          required
          autoComplete="email"
          onBlur={onEmailBlur}
          onChange={onEmailChange}
          ref={emailInputRef}
        />
        {fetcher.data?.message && (
          <div className="validation-error">{fetcher.data?.message}</div>
        )}
      </div>
      {includeDemoFields && (
        <div className="field">
          <label htmlFor="phone">Phone number</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            aria-describedby={phoneError ? 'phone-error' : undefined}
            onBlur={validatePhone}
            onChange={onPhoneChange}
            ref={phoneInputRef}
          />
          {phoneError && (
            <div id="phone-error" className="validation-error">
              {phoneError}
            </div>
          )}
        </div>
      )}
      <div className="field">
        <label htmlFor="company">
          {labels.company} <span className="required">*</span>
        </label>
        <input type="text" size={42} name="company" required />
      </div>

      <div className="field">
        <label htmlFor="hq_country">
          {labels.headquarters} <span className="required">*</span>
        </label>
        <select name="hq_country" required defaultValue={defaultCountryCode}>
          {countries.map(({ name, translatedName }) => (
            <option key={name} value={name}>
              {translatedName}
            </option>
          ))}
        </select>
      </div>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="business_website_hp">Company website</label>
        <input
          type="url"
          id="business_website_hp"
          name="business_website"
          placeholder="https://www.yourcompany.com"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      {customReferralField ?? (
        <>
          <fieldset className="choices">
            <legend>
              {labels.heardFrom} <span className="required">*</span>
            </legend>
            <Select
              includeBlank
              choices={leadSource}
              onChange={handleSetSource}
              name="how_did_you_hear_about_us__c"
              required
              value={source}
            />
          </fieldset>

          {source === leadSourceReferral && (
            <div className="field">
              <label htmlFor="who_referred_you__c">
                {labels.whoReferred} <span className="required">*</span>
              </label>
              <input
                type="text"
                size={42}
                name="who_referred_you__c"
                required
              />
            </div>
          )}
          {includeDemoFields && source === 'Other' && (
            <div className="field">
              <label htmlFor="how_did_you_hear_about_us__other">
                Please specify
              </label>
              <input
                id="how_did_you_hear_about_us__other"
                type="text"
                size={42}
                name="how_did_you_hear_about_us__other"
              />
            </div>
          )}
        </>
      )}

      {children}

      <button type="submit" disabled={!canSubmit}>
        {buttonLabel}
      </button>
    </form>
  )
}
